/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, react-hooks/set-state-in-effect, react-hooks/exhaustive-deps, react/no-children-prop */
import { faChevronDown, faChevronUp, faEllipsisVertical, faFile, faPencil, faPlus, faReceipt, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { RefreshButton, useAppBar, useDialogs } from "components";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Child, Receipt, ServicePeriod } from "../../../models";
import {
  useDeleteReceiptMutation,
  useDeleteServicePeriodMutation,
  useGetAllChildrenFilteredQuery,
  useGetAllFamiliesQuery,
  useGetAllReceiptsFilteredQuery,
  useGetAllServicePeriodsQuery,
  useGetReceiptHtmlByIdQuery,
} from "../../../redux/apiStore";
import { formatAsShortDate, getChildImageUrlFromChild, parseDate } from "../../../utils";
import { EditReceiptDrawer } from "./EditReceiptDrawer";
import { EditServicePeriodDrawer } from "./EditServicePeriodDrawer";
import { GenerateTaxReportDrawer } from "./GenerateTaxReportDrawer";
import { ReceiptPreviewDrawer } from "./ReceiptPreviewDrawer";

function startOfWeekMonday(value: Date): Date {
  const dayOfWeek = value.getDay();
  const daysSinceMonday = (dayOfWeek - 1 + 7) % 7;
  const date = new Date(value);
  date.setDate(date.getDate() - daysSinceMonday);
  return date;
}

function countBusinessWeeksEnrolled(child: Child, periodStart: Date, periodEnd: Date): number {
  const enrollmentStart = child.enrollmentStartCentralDateTime ? new Date(child.enrollmentStartCentralDateTime) : new Date(-8640000000000000);
  const enrollmentEnd = child.enrollmentEndCentralDateTime ? new Date(child.enrollmentEndCentralDateTime) : new Date(8640000000000000);

  const effectiveStart = periodStart > enrollmentStart ? periodStart : enrollmentStart;
  const effectiveEnd = periodEnd < enrollmentEnd ? periodEnd : enrollmentEnd;

  if (effectiveStart > effectiveEnd) {
    return 0;
  }

  let count = 0;
  let weekStart = startOfWeekMonday(effectiveStart);

  while (weekStart <= effectiveEnd) {
    const businessStart = weekStart;
    const businessEnd = new Date(weekStart);
    businessEnd.setDate(businessEnd.getDate() + 4);

    const overlapStart = effectiveStart > businessStart ? effectiveStart : businessStart;
    const overlapEnd = effectiveEnd < businessEnd ? effectiveEnd : businessEnd;

    if (overlapStart <= overlapEnd) {
      count++;
    }

    weekStart = new Date(weekStart);
    weekStart.setDate(weekStart.getDate() + 7);
  }

  return count;
}

function PaymentsTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const families = useGetAllFamiliesQuery({});
  const allChildren = useGetAllChildrenFilteredQuery({ showInactive: true });
  const servicePeriods = useGetAllServicePeriodsQuery({});
  const [deleteServicePeriod] = useDeleteServicePeriodMutation();
  const [deleteReceipt] = useDeleteReceiptMutation();
  const dialogs = useDialogs();
  const theme = useTheme();
  const _isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedReceiptId, setSelectedReceiptId] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<ServicePeriod | null>(null);
  const [isEditDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);
  const [isReceiptDrawerOpen, setReceiptDrawerOpen] = useState<boolean>(false);
  const [isTaxReportDrawerOpen, setTaxReportDrawerOpen] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedPeriodIds, setExpandedPeriodIds] = useState<Set<number>>(new Set());
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const receiptHtmlQuery = useGetReceiptHtmlByIdQuery({ id: selectedReceiptId ?? undefined }, { skip: !selectedReceiptId });

  // Get family ID from URL params
  const urlFamilyId = useMemo(() => {
    const param = searchParams.get("familyId");
    return param ? parseInt(param, 10) : undefined;
  }, [searchParams]);

  // Determine which families are active (have at least one active child)
  const activeFamilyIds = useMemo(() => {
    if (!allChildren.data) return new Set<number>();
    return new Set(allChildren.data.filter((child: Child) => child.isActive && !child.deleted).map((child: Child) => child.familyId));
  }, [allChildren.data]);

  // Group families: active first, then inactive
  const activeFamilies = useMemo(() => {
    return (families.data ?? []).filter((f) => !f.deleted && activeFamilyIds.has(f.id)).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [families.data, activeFamilyIds]);

  const inactiveFamilies = useMemo(() => {
    return (families.data ?? []).filter((f) => !f.deleted && !activeFamilyIds.has(f.id)).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [families.data, activeFamilyIds]);

  // Initialize with first active family, or first family overall
  const defaultFamilyId = useMemo(() => {
    if (activeFamilies.length > 0) return activeFamilies[0].id;
    if (inactiveFamilies.length > 0) return inactiveFamilies[0].id;
    return undefined;
  }, [activeFamilies, inactiveFamilies]);

  const [selectedFamilyId, setSelectedFamilyId] = useState<number | undefined>(urlFamilyId ?? defaultFamilyId);
  const receipts = useGetAllReceiptsFilteredQuery({ familyId: selectedFamilyId });

  // Update selected family when URL param or default changes
  useEffect(() => {
    if (urlFamilyId !== undefined) {
      setSelectedFamilyId(urlFamilyId);
    } else if (defaultFamilyId !== undefined) {
      setSelectedFamilyId(defaultFamilyId);
    }
  }, [urlFamilyId, defaultFamilyId]);

  // Update URL when selected family changes
  useEffect(() => {
    if (selectedFamilyId !== undefined) {
      setSearchParams({ familyId: selectedFamilyId.toString() }, { replace: true });
    }
  }, [selectedFamilyId, setSearchParams]);

  // Reset archived visibility when family changes
  useEffect(() => {
    setShowArchived(false);
  }, [selectedFamilyId]);

  // Filter service periods by selected family
  const filteredPeriods = useMemo(() => {
    if (!servicePeriods.data || !selectedFamilyId) return [];
    return servicePeriods.data.filter((p) => p.family?.id === selectedFamilyId);
  }, [servicePeriods.data, selectedFamilyId]);

  // Sort by start date descending
  const orderedData = _.orderBy(filteredPeriods, [(x) => parseDate(x.startCentralDateTime)?.getTime() ?? 0], ["desc"]);

  // Separate archived periods (paid AND past end date)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isArchivedPeriod = (period: ServicePeriod): boolean => {
    const endDate = parseDate(period.endCentralDateTime);
    if (!endDate) return false;
    endDate.setHours(0, 0, 0, 0);
    return !!period.receiptId && endDate < today;
  };

  const visibleData = showArchived ? orderedData : orderedData.filter((p) => !isArchivedPeriod(p));
  const archivedCount = orderedData.filter(isArchivedPeriod).length;

  const isFetching = servicePeriods.isFetching;

  const getChildrenDisplay = (period: ServicePeriod) => {
    const children = _.orderBy(
      period.lines.filter((line) => !line.deleted && line.child).map((line) => line.child!),
      [(child) => (child.firstName ?? "").toLowerCase()],
      ["asc"],
    );

    return (
      <Stack direction="row" gap={2} rowGap={0.5} flexWrap="wrap">
        {children.map((child) => (
          <Stack key={child.id} direction="row" alignItems="center" gap={1}>
            <Avatar
              alt={child.firstName}
              src={getChildImageUrlFromChild(child)}
              sx={{
                width: 32,
                height: 32,
              }}
            />
            <Box sx={{ fontSize: "0.875rem" }}>{child.firstName}</Box>
          </Stack>
        ))}
      </Stack>
    );
  };

  const getEstimatedTotal = (period: ServicePeriod) => {
    const total = period.lines.filter((line) => !line.deleted).reduce((sum, line) => sum + line.estimatedTotal, 0);
    return total.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  const formatCurrency = (amount: number) => amount.toLocaleString("en-US", { style: "currency", currency: "USD" });

  const childById = useMemo(() => {
    const map = new Map<number, Child>();
    (allChildren.data ?? []).forEach((child) => map.set(child.id, child));
    return map;
  }, [allChildren.data]);

  const receiptById = useMemo(() => {
    const map = new Map<number, Receipt>();
    (receipts.data ?? []).forEach((receipt) => map.set(receipt.id, receipt));
    return map;
  }, [receipts.data]);

  const getDateRangeDisplay = (period: ServicePeriod) => {
    const startDate = formatAsShortDate(parseDate(period.startCentralDateTime));
    const endDate = formatAsShortDate(parseDate(period.endCentralDateTime));
    return `${startDate} - ${endDate}`;
  };

  const getStatus = (period: ServicePeriod): "PAID" | "UPCOMING" | "PAST DUE" => {
    if (period.receiptId) {
      return "PAID";
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = parseDate(period.startCentralDateTime);
    if (!startDate) return "UPCOMING";

    if (today >= startDate) {
      return "PAST DUE";
    }
    return "UPCOMING";
  };

  const getStatusChip = (status: "PAID" | "UPCOMING" | "PAST DUE") => {
    switch (status) {
      case "PAID":
        return <Chip label="PAID" variant="outlined" color="success" size="small" />;
      case "UPCOMING":
        return <Chip label="UPCOMING" variant="outlined" color="info" size="small" />;
      case "PAST DUE":
        return <Chip label="PAST DUE" variant="outlined" color="error" size="small" />;
    }
  };

  // Memoize period details to avoid recalculation on every render
  const periodDetailsMap = useMemo(() => {
    const map = new Map<
      number,
      {
        status: "PAID" | "UPCOMING" | "PAST DUE";
        dateRange: string;
        total: string;
        children: any[];
        dateRangeDisplay: string;
        startDate: Date | null;
        endDate: Date | null;
        linesByChildId: Map<number, any>;
        detailRows: {
          key: string;
          avatarChildId?: number;
          label: string;
          subLabel: string;
          amount: string;
        }[];
      }
    >();

    orderedData.forEach((period) => {
      const status = getStatus(period);
      const dateRangeDisplay = getDateRangeDisplay(period);
      const estimatedTotal = getEstimatedTotal(period);
      const periodStart = parseDate(period.startCentralDateTime);
      const periodEnd = parseDate(period.endCentralDateTime);
      const receipt = period.receiptId ? receiptById.get(period.receiptId) : undefined;

      const children = _.orderBy(
        period.lines.filter((line) => !line.deleted && line.child).map((line) => line.child!),
        [(child) => (child.firstName ?? "").toLowerCase()],
        ["asc"],
      );

      // Pre-build a map of lines by child ID for O(1) lookups
      const linesByChildId = new Map();
      period.lines.forEach((line) => {
        if (!line.deleted && line.childId) {
          linesByChildId.set(line.childId, line);
        }
      });

      const receiptLines = receipt?.lines?.filter((line) => !line.deleted) ?? [];
      const receiptLineTotal = receiptLines.reduce((sum, line) => sum + line.amount, 0);
      const adjustment = receipt?.paymentAdjustmentAmount ?? 0;
      const hasAdjustment = !!receipt && (adjustment !== 0 || !!receipt.paymentAdjustmentDescription?.trim());

      const detailRows = receipt
        ? [
            ...receiptLines.map((line, index) => {
              const child = childById.get(line.childId);
              const weeks = periodStart && periodEnd && child ? countBusinessWeeksEnrolled(child, periodStart, periodEnd) : 0;
              return {
                key: `${period.id}-receipt-line-${line.id || index}`,
                avatarChildId: child?.id,
                label: child?.firstName ?? `Child #${line.childId}`,
                subLabel: `${weeks} week${weeks !== 1 ? "s" : ""}`,
                amount: formatCurrency(line.amount),
              };
            }),
            ...(hasAdjustment
              ? [
                  {
                    key: `${period.id}-receipt-adjustment`,
                    label: "Adjustment",
                    subLabel: receipt.paymentAdjustmentDescription?.trim() || "Adjustment",
                    amount: formatCurrency(adjustment),
                  },
                ]
              : []),
          ]
        : children.map((child) => {
            const weeks = periodStart && periodEnd ? countBusinessWeeksEnrolled(child, periodStart, periodEnd) : 0;
            const line = linesByChildId.get(child.id);
            const amount = line?.estimatedTotal ?? 0;
            return {
              key: `${period.id}-${child.id}`,
              avatarChildId: child.id,
              label: child.firstName,
              subLabel: `${weeks} week${weeks !== 1 ? "s" : ""}`,
              amount: formatCurrency(amount),
            };
          });

      const total = receipt ? formatCurrency(receiptLineTotal + adjustment) : estimatedTotal;

      map.set(period.id, {
        status,
        dateRange: dateRangeDisplay,
        total,
        children,
        dateRangeDisplay,
        startDate: periodStart,
        endDate: periodEnd,
        linesByChildId,
        detailRows,
      });
    });

    return map;
  }, [orderedData, getStatus, getDateRangeDisplay, getEstimatedTotal, childById, receiptById]);

  const hasFamilyPastDue = (familyId: number) => {
    if (!servicePeriods.data) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return servicePeriods.data.some((period) => {
      if (period.family?.id !== familyId) return false;
      if (period.receiptId) return false;
      const startDate = parseDate(period.startCentralDateTime);
      return startDate && today >= startDate;
    });
  };

  const loadingRow = (
    <TableRow>
      <TableCell sx={{ p: 1 }}>
        <Skeleton width={32} height={32} />
      </TableCell>
      <TableCell>
        <Skeleton width={180} height={28} />
      </TableCell>
      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
        <Skeleton width={220} height={28} />
      </TableCell>
      <TableCell>
        <Skeleton width={80} height={28} />
      </TableCell>
      <TableCell align="center">
        <Skeleton width={100} height={28} />
      </TableCell>
      <TableCell align="center">
        <Skeleton width={44} height={44} />
      </TableCell>
    </TableRow>
  );

  const handleFamilyChange = (event: SelectChangeEvent<number>) => {
    setSelectedFamilyId(Number(event.target.value));
  };

  const togglePeriodExpanded = useCallback((periodId: number) => {
    setExpandedPeriodIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(periodId)) {
        newSet.delete(periodId);
      } else {
        newSet.add(periodId);
      }
      return newSet;
    });
  }, []);

  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>, period: ServicePeriod) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedPeriod(period);
  }, []);

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleAddServicePeriod = () => {
    setSelectedPeriod(null);
    setEditDrawerOpen(true);
  };

  const handleOpenTaxReportDrawer = () => {
    setTaxReportDrawerOpen(true);
  };

  const handleEditServicePeriod = () => {
    if (!selectedPeriod) {
      return;
    }

    setEditDrawerOpen(true);
    closeMenu();
  };

  const handleDeleteServicePeriod = async () => {
    if (!selectedPeriod) {
      return;
    }

    try {
      const confirmed = await dialogs.confirm("Are you sure you want to delete this service period?");
      if (!confirmed) {
        closeMenu();
        return;
      }

      await deleteServicePeriod({ id: selectedPeriod.id }).unwrap();
      closeMenu();
    } catch (err) {
      console.error("Failed to delete service period", err);
      closeMenu();
    }
  };

  const handleDeleteReceipt = async () => {
    if (!selectedPeriod?.receiptId) {
      return;
    }

    try {
      const confirmed = await dialogs.confirm("Are you sure you want to delete this receipt?");
      if (!confirmed) {
        closeMenu();
        return;
      }

      await deleteReceipt({ id: selectedPeriod.receiptId }).unwrap();
      servicePeriods.refetch();
      receipts.refetch();
      closeMenu();
    } catch (err) {
      console.error("Failed to delete receipt", err);
      closeMenu();
    }
  };

  const handleCreateReceipt = () => {
    if (!selectedPeriod || selectedPeriod.receiptId) {
      return;
    }

    setReceiptDrawerOpen(true);
    closeMenu();
  };

  const handleEditReceipt = () => {
    if (!selectedPeriod?.receiptId) {
      return;
    }

    setReceiptDrawerOpen(true);
    closeMenu();
  };

  return (
    <Stack gap={2}>
      <Stack
        gap={1}
        sx={{
          display: { xs: "flex", md: "flex" },
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: { xs: "stretch", md: "flex-end" },
        }}
      >
        <FormControl
          sx={{
            flex: { xs: 1, md: "0 0 auto" },
            minWidth: { xs: "100%", md: 280 },
            "& .MuiOutlinedInput-root": { height: 50 },
          }}
        >
          <InputLabel>Family</InputLabel>
          <Select value={selectedFamilyId ?? ""} label="Family" onChange={handleFamilyChange}>
            {activeFamilies.length > 0 && [
              <MenuItem key="active-header" disabled sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Active Families
              </MenuItem>,
              activeFamilies.map((family) => (
                <MenuItem key={family.id} value={family.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <span>{family.name}</span>
                    {hasFamilyPastDue(family.id) && <Chip label="PAST DUE" variant="outlined" color="error" size="small" />}
                  </Box>
                </MenuItem>
              )),
            ]}
            {inactiveFamilies.length > 0 && [
              <MenuItem key="inactive-header" disabled sx={{ fontWeight: "bold", fontSize: "0.875rem" }}>
                Inactive Families
              </MenuItem>,
              inactiveFamilies.map((family) => (
                <MenuItem key={family.id} value={family.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <span>{family.name}</span>
                    {hasFamilyPastDue(family.id) && <Chip label="PAST DUE" variant="outlined" color="error" size="small" />}
                  </Box>
                </MenuItem>
              )),
            ]}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }} />
        <Stack
          direction={{ xs: "row", md: "row" }}
          spacing={1}
          sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { xs: "100%", md: "auto" }, gap: { xs: 1, md: 1 } }}
        >
          <Button
            variant="outlined"
            onClick={handleOpenTaxReportDrawer}
            startIcon={<FontAwesomeIcon icon={faFile} />}
            sx={{ flex: { xs: 1, md: "0 0 auto" }, height: 50 }}
          >
            Tax Report
          </Button>
          <Button
            variant="outlined"
            onClick={handleAddServicePeriod}
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            sx={{ flex: { xs: 1, md: "0 0 auto" }, height: 50 }}
          >
            <Box sx={{ display: { xs: "block", md: "none" } }}>Add</Box>
            <Box sx={{ display: { xs: "none", md: "block" } }}>Add Service Period</Box>
          </Button>
        </Stack>
      </Stack>
      <TableContainer component={Paper}>
        <Table aria-label="payments table">
          <TableHead>
            <TableRow>
              <TableCell width={60} sx={{ p: 1 }}></TableCell>
              <TableCell>
                <Box sx={{ display: { xs: "block", md: "none" } }}>Dates</Box>
                <Box sx={{ display: { xs: "none", md: "block" } }}>Service Dates</Box>
              </TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Children</TableCell>
              <TableCell>
                <Box sx={{ display: { xs: "block", md: "none" } }}>Total</Box>
                <Box sx={{ display: { xs: "none", md: "block" } }}>Total</Box>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={40} sx={{ p: 1 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching &&
              [0, 1, 2].map((index) => (
                <TableRow key={`loading-${index}`}>
                  <TableCell align="center" sx={{ p: 1 }}>
                    <Skeleton width={44} height={44} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={180} height={28} />
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    <Skeleton width={220} height={28} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={80} height={28} />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton width={100} height={28} />
                  </TableCell>
                  <TableCell sx={{ p: 1 }}>
                    <Skeleton width={32} height={32} />
                  </TableCell>
                </TableRow>
              ))}
            {visibleData.map((period) => {
              const details = periodDetailsMap.get(period.id);
              if (!details) return null;

              const { status, dateRange, total, detailRows } = details;
              const isExpanded = expandedPeriodIds.has(period.id);

              const rows: React.ReactNode[] = [
                <TableRow key={period.id} hover>
                  <TableCell align="center" sx={{ p: { xs: 0.5, md: 1 } }}>
                    <IconButton sx={{ width: 44, height: 44 }} onClick={(event) => openMenu(event, period)}>
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ py: { xs: 0.5, md: 1 }, px: { xs: 0.5, md: 1 } }}>{dateRange}</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" }, py: 1 }}>{getChildrenDisplay(period)}</TableCell>
                  <TableCell sx={{ py: { xs: 0.5, md: 1 }, px: { xs: 0.5, md: 1 } }}>{total}</TableCell>
                  <TableCell sx={{ py: { xs: 0.5, md: 1 }, px: { xs: 0.5, md: 1 } }}>{getStatusChip(status)}</TableCell>
                  <TableCell align="center" sx={{ p: { xs: 0.5, md: 1 } }}>
                    <IconButton size="small" onClick={() => togglePeriodExpanded(period.id)} sx={{ width: 32, height: 32 }}>
                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} size="sm" />
                    </IconButton>
                  </TableCell>
                </TableRow>,
              ];

              if (isExpanded) {
                rows.push(
                  ...detailRows.map((detailRow) => {
                    return (
                      <TableRow key={detailRow.key} sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                        <TableCell sx={{ p: { xs: 0.5, md: 1 }, display: { xs: "none", md: "table-cell" } }}></TableCell>
                        <TableCell sx={{ py: { xs: 0.5, md: 1 }, pr: { xs: 1, md: 2 }, display: { xs: "none", md: "table-cell" } }} colSpan={1}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            {detailRow.avatarChildId ? (
                              <Avatar
                                alt={detailRow.label}
                                src={getChildImageUrlFromChild(childById.get(detailRow.avatarChildId))}
                                sx={{
                                  width: 28,
                                  height: 28,
                                }}
                              />
                            ) : (
                              <Box sx={{ width: 28, height: 28 }} />
                            )}
                            <Box sx={{ fontSize: "0.875rem" }}>{detailRow.label}</Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 0.5, md: 1 }, pr: { xs: 1, md: 2 }, display: { xs: "table-cell", md: "none" } }} colSpan={3}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            {detailRow.avatarChildId ? (
                              <Avatar
                                alt={detailRow.label}
                                src={getChildImageUrlFromChild(childById.get(detailRow.avatarChildId))}
                                sx={{
                                  width: 28,
                                  height: 28,
                                }}
                              />
                            ) : (
                              <Box sx={{ width: 28, height: 28 }} />
                            )}
                            <Box>
                              <Box sx={{ fontSize: "0.875rem" }}>{detailRow.label}</Box>
                              <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{detailRow.subLabel}</Box>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 0.5, md: 1 }, pr: { xs: 1, md: 2 }, display: { xs: "none", md: "table-cell" } }}>
                          <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>{detailRow.subLabel}</Box>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 0.5, md: 1 }, pr: { xs: 1, md: 2 } }}>
                          <Box sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{detailRow.amount}</Box>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 0.5, md: 1 }, display: { xs: "none", md: "table-cell" } }}></TableCell>
                        <TableCell sx={{ p: { xs: 0.5, md: 1 } }}></TableCell>
                      </TableRow>
                    );
                  }),
                );
              }

              return rows;
            })}
          </TableBody>
          {archivedCount > 0 && !showArchived && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={6} sx={{ p: 0 }}>
                  <Button
                    fullWidth
                    onClick={() => setShowArchived(true)}
                    sx={{
                      py: 1.5,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    Show Archived Service Periods ({archivedCount})
                  </Button>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        {!selectedPeriod?.receiptId && (
          <MenuItem onClick={handleCreateReceipt}>
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 12 }} />
            Create Receipt
          </MenuItem>
        )}
        {selectedPeriod?.receiptId && (
          <MenuItem onClick={handleEditReceipt}>
            <FontAwesomeIcon icon={faPencil} style={{ marginRight: 12 }} />
            Edit Receipt
          </MenuItem>
        )}
        {selectedPeriod?.receiptId && (
          <MenuItem
            onClick={() => {
              setSelectedReceiptId(selectedPeriod.receiptId!);
              closeMenu();
            }}
          >
            <FontAwesomeIcon icon={faReceipt} style={{ marginRight: 12 }} />
            View Receipt
          </MenuItem>
        )}
        {selectedPeriod?.receiptId && (
          <MenuItem onClick={handleDeleteReceipt}>
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: 12 }} />
            Delete Receipt
          </MenuItem>
        )}
        <MenuItem onClick={handleEditServicePeriod}>
          <FontAwesomeIcon icon={faPencil} style={{ marginRight: 12 }} />
          Edit Service Period
        </MenuItem>
        <MenuItem onClick={handleDeleteServicePeriod}>
          <FontAwesomeIcon icon={faTrash} style={{ marginRight: 12 }} />
          Delete Service Period
        </MenuItem>
      </Menu>

      <EditServicePeriodDrawer
        open={isEditDrawerOpen}
        families={activeFamilies}
        children={allChildren.data ?? []}
        servicePeriod={selectedPeriod}
        defaultFamilyId={selectedFamilyId}
        onSaved={() => {
          servicePeriods.refetch();
        }}
        onClose={() => {
          setEditDrawerOpen(false);
          setSelectedPeriod(null);
        }}
      />

      <EditReceiptDrawer
        open={isReceiptDrawerOpen}
        servicePeriod={selectedPeriod}
        children={allChildren.data ?? []}
        onSaved={(receiptId) => {
          servicePeriods.refetch();
          receipts.refetch();
          setSelectedReceiptId(receiptId);
        }}
        onClose={() => {
          setReceiptDrawerOpen(false);
          setSelectedPeriod(null);
        }}
      />

      <ReceiptPreviewDrawer
        open={selectedReceiptId !== null}
        receiptId={selectedReceiptId}
        html={receiptHtmlQuery.data ?? ""}
        isLoadingHtml={receiptHtmlQuery.isFetching}
        onClose={() => setSelectedReceiptId(null)}
      />

      <GenerateTaxReportDrawer
        open={isTaxReportDrawerOpen}
        families={activeFamilies}
        defaultFamilyId={selectedFamilyId}
        onClose={() => setTaxReportDrawerOpen(false)}
      />
    </Stack>
  );
}

export function BookkeepingPage() {
  const appBar = useAppBar();
  const familiesQuery = useGetAllFamiliesQuery({});
  const childrenQuery = useGetAllChildrenFilteredQuery({});
  const servicePeriodsQuery = useGetAllServicePeriodsQuery({});
  const receiptsQuery = useGetAllReceiptsFilteredQuery({});

  const refreshBookkeeping = () => {
    Promise.all([familiesQuery.refetch(), childrenQuery.refetch(), servicePeriodsQuery.refetch(), receiptsQuery.refetch()]);
  };

  useEffect(() => {
    appBar.setDefaults();
    appBar.setTitle("Bookkeeping");
    appBar.setRightContent(<RefreshButton onRefresh={refreshBookkeeping} />);
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Stack gap={1}>
        <PaymentsTable />
      </Stack>
    </Box>
  );
}
